import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Save, X, LayoutTemplate } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tl = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;
const trDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('tr-TR') : '-');

export const ExpenseTemplateModal = ({ open, onOpenChange, orgId, sessions = [], onApplied }) => {
  const [templates, setTemplates] = useState([]);
  const [cats, setCats] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState('sessions');
  const [sessionIds, setSessionIds] = useState([]);
  const [generalDate, setGeneralDate] = useState(new Date().toISOString().slice(0, 10));
  const [applying, setApplying] = useState(false);
  const [manage, setManage] = useState(false);
  const [draft, setDraft] = useState({ id: null, name: '', description: '', items: [] });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/organization-expense-templates`);
      const list = res.data?.items || [];
      setTemplates(list);
      if (list.length && !selectedId) {
        setSelectedId(list[0].id);
        setItems(list[0].items.map(i => ({ ...i, enabled: true })));
      }
    } catch (e) { toast.error('Şablonlar yüklenemedi'); }
  }, [selectedId]);

  useEffect(() => {
    if (!open) return;
    load();
    axios.get(`${API}/organization-categories`).then(r => setCats(r.data?.expense || [])).catch(() => {});
  }, [open, load]);

  const pickTemplate = (id) => {
    setSelectedId(id);
    const t = templates.find(x => x.id === id);
    setItems((t?.items || []).map(i => ({ ...i, enabled: true })));
  };

  const apply = async () => {
    const active = items.filter(i => i.enabled && i.title && i.category);
    if (!active.length) { toast.error('En az bir kalem seçin'); return; }
    if (scope === 'sessions' && sessionIds.length === 0) { toast.error('En az bir oturum seçin'); return; }
    setApplying(true);
    try {
      const res = await axios.post(`${API}/organizations/${orgId}/apply-expense-template`, {
        template_id: selectedId,
        template_name: templates.find(t => t.id === selectedId)?.name,
        items: active.map(i => ({ title: i.title, category: i.category, amount: Number(i.amount) || 0, notes: i.notes })),
        scope,
        session_ids: scope === 'sessions' ? sessionIds : [],
        date: generalDate,
      });
      toast.success(res.data?.message || 'Uygulandı');
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Şablon uygulanamadı');
    } finally { setApplying(false); }
  };

  const saveTemplate = async () => {
    if (!draft.name.trim()) { toast.error('Şablon adı girin'); return; }
    const payload = {
      name: draft.name, description: draft.description,
      items: draft.items.filter(i => i.title && i.category).map(i => ({ ...i, amount: Number(i.amount) || 0 })),
    };
    if (!payload.items.length) { toast.error('En az bir kalem (başlık + kategori) ekleyin'); return; }
    try {
      if (draft.id) await axios.put(`${API}/organization-expense-templates/${draft.id}`, payload);
      else await axios.post(`${API}/organization-expense-templates`, payload);
      toast.success('Şablon kaydedildi');
      setDraft({ id: null, name: '', description: '', items: [] });
      setManage(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kaydedilemedi'); }
  };

  const removeTemplate = async (t) => {
    if (!window.confirm(`"${t.name}" şablonu silinecek. Devam?`)) return;
    try {
      await axios.delete(`${API}/organization-expense-templates/${t.id}`);
      toast.success('Şablon silindi');
      if (selectedId === t.id) { setSelectedId(''); setItems([]); }
      await load();
    } catch (e) { toast.error('Silinemedi'); }
  };

  const total = items.filter(i => i.enabled).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const multiplier = scope === 'sessions' ? sessionIds.length : 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="org-template-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="w-4 h-4" /> Gider Şablonu</DialogTitle>
          <DialogDescription className="text-xs">
            Şablon kalemleri <strong>bekleyen gider</strong> olarak eklenir; kasaya hiçbir hareket yazılmaz.
            Ödeme yapıldığında ilgili kayıttan "Ödeme Yap" ile kasaya işlenir.
          </DialogDescription>
        </DialogHeader>

        {manage ? (
          <div className="space-y-3" data-testid="org-template-manage">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Şablon Adı *</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Maç Günü Gider Şablonu" data-testid="org-template-name" /></div>
              <div><Label className="text-xs">Açıklama</Label><Input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="org-template-desc" /></div>
            </div>
            <div className="space-y-1.5">
              {draft.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                  <Input className="col-span-4 h-8" placeholder="Kalem adı" value={it.title} onChange={(e) => setDraft({ ...draft, items: draft.items.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} data-testid={`org-template-item-title-${i}`} />
                  <div className="col-span-5">
                    <Select value={it.category} onValueChange={(v) => setDraft({ ...draft, items: draft.items.map((x, j) => j === i ? { ...x, category: v } : x) })}>
                      <SelectTrigger className="h-8" data-testid={`org-template-item-cat-${i}`}><SelectValue placeholder="Kategori" /></SelectTrigger>
                      <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2 h-8" type="number" placeholder="₺" value={it.amount} onChange={(e) => setDraft({ ...draft, items: draft.items.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) })} data-testid={`org-template-item-amount-${i}`} />
                  <Button size="sm" variant="ghost" className="col-span-1 h-8 w-8 p-0 text-rose-600" onClick={() => setDraft({ ...draft, items: draft.items.filter((_, j) => j !== i) })}><X className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setDraft({ ...draft, items: [...draft.items, { title: '', category: '', amount: '' }] })} data-testid="org-template-item-add">
                <Plus className="w-3.5 h-3.5 mr-1" /> Kalem Ekle
              </Button>
            </div>
            <div className="flex gap-2">
              <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={saveTemplate} data-testid="org-template-save"><Save className="w-3.5 h-3.5 mr-1" /> Şablonu Kaydet</Button>
              <Button variant="outline" onClick={() => { setManage(false); setDraft({ id: null, name: '', description: '', items: [] }); }}>Vazgeç</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Şablon</Label>
                <Select value={selectedId} onValueChange={pickTemplate}>
                  <SelectTrigger data-testid="org-template-select"><SelectValue placeholder="Şablon seç" /></SelectTrigger>
                  <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.items?.length || 0} kalem)</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button size="sm" variant="outline" className="h-9" onClick={() => { const t = templates.find(x => x.id === selectedId); setDraft(t ? { id: t.id, name: t.name, description: t.description || '', items: t.items.map(i => ({ ...i })) } : { id: null, name: '', description: '', items: [] }); setManage(true); }} data-testid="org-template-edit-btn">Düzenle</Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => { setDraft({ id: null, name: '', description: '', items: [{ title: '', category: '', amount: '' }] }); setManage(true); }} data-testid="org-template-new-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Yeni</Button>
              {templates.find(t => t.id === selectedId) && (
                <Button size="sm" variant="outline" className="h-9 text-rose-600" onClick={() => removeTemplate(templates.find(t => t.id === selectedId))} data-testid="org-template-delete-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
              )}
            </div>

            <div className="rounded-lg border divide-y" data-testid="org-template-items">
              {items.length === 0 ? <p className="text-xs text-slate-400 p-4 text-center">Şablon seçin</p> : items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 p-2">
                  <input type="checkbox" className="h-4 w-4" checked={!!it.enabled}
                    onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))}
                    data-testid={`org-template-apply-check-${i}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{it.title}</p>
                    <p className="text-[11px] text-slate-500">{it.category}</p>
                  </div>
                  <Input type="number" className="h-8 w-28" value={it.amount}
                    onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                    data-testid={`org-template-apply-amount-${i}`} />
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-2.5 space-y-2">
              <div className="flex gap-1">
                <Button size="sm" variant={scope === 'sessions' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setScope('sessions')} data-testid="org-template-scope-sessions">Seçili Oturumlara</Button>
                <Button size="sm" variant={scope === 'general' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setScope('general')} data-testid="org-template-scope-general">Organizasyon Geneline</Button>
              </div>
              {scope === 'sessions' ? (
                <>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setSessionIds(sessions.map(s => s.id))} data-testid="org-template-select-all">Tümünü Seç ({sessions.length})</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setSessionIds([])}>Temizle</Button>
                  </div>
                  <div className="max-h-36 overflow-y-auto space-y-1 bg-white rounded border p-1.5" data-testid="org-template-session-list">
                    {sessions.length === 0 ? <p className="text-[11px] text-slate-400 text-center py-2">Oturum yok</p> : sessions.map(s => (
                      <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" className="h-3.5 w-3.5" checked={sessionIds.includes(s.id)}
                          onChange={(e) => setSessionIds(e.target.checked ? [...sessionIds, s.id] : sessionIds.filter(x => x !== s.id))}
                          data-testid={`org-template-session-${s.id}`} />
                        {trDate(s.date)} · {s.start_time}–{s.end_time} · {s.salon_name || ''}
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <div><Label className="text-[11px]">Kayıt tarihi</Label><Input type="date" className="h-8 w-40" value={generalDate} onChange={(e) => setGeneralDate(e.target.value)} data-testid="org-template-general-date" /></div>
              )}
              <p className="text-[11px] text-emerald-900">
                {items.filter(i => i.enabled).length} kalem × {multiplier} = <strong>{items.filter(i => i.enabled).length * multiplier} kayıt</strong> ·
                toplam <strong>{tl(total * multiplier)}</strong> bekleyen gider
              </p>
            </div>

            <Button onClick={apply} disabled={applying} className="w-full bg-rose-700 hover:bg-rose-800" data-testid="org-template-apply-submit">
              {applying ? 'Uygulanıyor...' : 'Şablonu Uygula (bekleyen gider olarak ekle)'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
